import {
  PrimaryButton,
  SecondaryButton,
  Space,
  TertiaryButton,
  Text,
  TextLabel,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { FooterBarStyled } from './styles/FooterBarStyled'

export interface IReconciliationFooterProps {
  currencyName: string
  /** Selection mode (BHV-031) vs idle balances mode (BHV-036) */
  hasSelection: boolean
  selectedCcCount: number
  selectedStatementCount: number
  selectionTotal: number
  selectionVariance: number
  isMatchDisabled: boolean
  isMatching: boolean
  onDeselectAll: () => void
  onMatch: () => void
  endingBalance: number
  clearedBalance: number
  overallVariance: number
  isSubmitDisabled: boolean
  onViewMatched: () => void
  onSubmit: () => void
}

export const ReconciliationFooter = ({
  currencyName,
  hasSelection,
  selectedCcCount,
  selectedStatementCount,
  selectionTotal,
  selectionVariance,
  isMatchDisabled,
  isMatching,
  onDeselectAll,
  onMatch,
  endingBalance,
  clearedBalance,
  overallVariance,
  isSubmitDisabled,
  onViewMatched,
  onSubmit,
}: IReconciliationFooterProps) => {
  const intl = useProcurifyIntl()

  return (
    <FooterBarStyled>
      {hasSelection ? (
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' align='center'>
            <Text autoMargin={false}>
              {intl.formatMessage(messages.ccItemsSelected, {
                count: selectedCcCount,
              })}
            </Text>
            <Text color='textSecondary' autoMargin={false}>
              {intl.formatMessage(messages.matchedWith)}
            </Text>
            <Text autoMargin={false}>
              {intl.formatMessage(messages.statementItemsSelected, {
                count: selectedStatementCount,
              })}
            </Text>
          </Space>
          <Space direction='horizontal' align='center'>
            <Space size='small' align='end'>
              <Text bold autoMargin={false}>
                {selectionTotal.toFixed(2)} {currencyName}
              </Text>
              <TextLabel>{intl.formatMessage(messages.totalLabel)}</TextLabel>
            </Space>
            <Space size='small' align='end'>
              <Text
                bold
                color={selectionVariance !== 0 ? 'danger' : 'success'}
                autoMargin={false}
              >
                {Math.abs(selectionVariance).toFixed(2)} {currencyName}
              </Text>
              <TextLabel>
                {intl.formatMessage(messages.varianceLabel)}
              </TextLabel>
            </Space>
            <SecondaryButton type='button' onClick={onDeselectAll}>
              {intl.formatMessage(messages.deselectAllButton)}
            </SecondaryButton>
            {/* Disabled while variance ≠ 0 or Invalid Currency (BHV-031/032) */}
            <PrimaryButton
              type='button'
              disabled={isMatchDisabled || isMatching}
              onClick={onMatch}
            >
              {intl.formatMessage(messages.matchButton)}
            </PrimaryButton>
          </Space>
        </Space>
      ) : (
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' size='large' align='center'>
            <Space size='small'>
              <TextLabel>
                {intl.formatMessage(messages.endingBalanceLabel)}
              </TextLabel>
              <Text bold autoMargin={false}>
                {endingBalance.toFixed(2)} {currencyName}
              </Text>
            </Space>
            <Space size='small'>
              <TextLabel>
                {intl.formatMessage(messages.clearedBalanceLabel)}
              </TextLabel>
              <Text bold autoMargin={false}>
                {clearedBalance.toFixed(2)} {currencyName}
              </Text>
            </Space>
            <Space size='small'>
              <TextLabel>
                {intl.formatMessage(messages.varianceLabel)}
              </TextLabel>
              <Text
                bold
                color={overallVariance !== 0 ? 'danger' : 'success'}
                autoMargin={false}
              >
                {overallVariance.toFixed(2)} {currencyName}
              </Text>
            </Space>
          </Space>
          <Space direction='horizontal' align='center'>
            <TertiaryButton type='button' onClick={onViewMatched}>
              {intl.formatMessage(messages.matchedItemsLink)}
            </TertiaryButton>
            {/* Disabled unless variance = 0 AND ≥1 confirmed match (BHV-036) */}
            <PrimaryButton
              type='button'
              disabled={isSubmitDisabled}
              onClick={onSubmit}
            >
              {intl.formatMessage(messages.submitButton)}
            </PrimaryButton>
          </Space>
        </Space>
      )}
    </FooterBarStyled>
  )
}

ReconciliationFooter.displayName = 'ReconciliationFooter'
